import { useEffect, useMemo, useState } from 'react'
import { updateAgentPostureExclusions } from '@/api/agents'
import { useAuth } from '@/auth/AuthContext'
import { Button } from '@/components/ui/button'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Label } from '@/components/ui/label'
import {
  POSTURE_EXCLUSION_OPTIONS,
  exclusionCount,
  exclusionLabels,
  normaliseExclusions,
} from '@/lib/postureExclusions'

function CollapsedExclusionSummary({ labels, note }) {
  if (!labels.length && !note) {
    return 'None — expand to ignore selected checks'
  }
  return (
    <div className="space-y-1">
      {labels.length > 0 ? (
        <ul className="list-disc space-y-0.5 pl-4">
          {labels.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      ) : null}
      {note ? <p className="pl-0.5 italic">{note}</p> : null}
    </div>
  )
}

export function PostureExclusionsCard({ agent, onSaved }) {
  const { isStaff } = useAuth()
  const [open, setOpen] = useState(false)
  const [exclusions, setExclusions] = useState(() =>
    normaliseExclusions(agent?.posture_exclusions),
  )
  const [note, setNote] = useState(agent?.posture_exclusion_note || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [savedMsg, setSavedMsg] = useState('')

  useEffect(() => {
    setExclusions(normaliseExclusions(agent?.posture_exclusions))
    setNote(agent?.posture_exclusion_note || '')
    setError('')
    setSavedMsg('')
    setOpen(false)
  }, [agent?.atera_id, agent?.posture_exclusions, agent?.posture_exclusion_note])

  const savedExclusions = useMemo(
    () => normaliseExclusions(agent?.posture_exclusions),
    [agent?.posture_exclusions],
  )
  const savedNote = agent?.posture_exclusion_note || ''
  const dirty =
    JSON.stringify(exclusions) !== JSON.stringify(savedExclusions) ||
    note.trim() !== savedNote.trim()

  const groups = useMemo(() => {
    const map = new Map()
    for (const opt of POSTURE_EXCLUSION_OPTIONS) {
      if (!map.has(opt.group)) map.set(opt.group, [])
      map.get(opt.group).push(opt)
    }
    return [...map.entries()]
  }, [])

  const activeCount = exclusionCount(exclusions)
  const labels = exclusionLabels(savedExclusions)
  const summary = (
    <CollapsedExclusionSummary labels={labels} note={savedNote.trim()} />
  )

  function toggle(key) {
    setExclusions((prev) => {
      const next = { ...prev }
      if (next[key]) delete next[key]
      else next[key] = true
      return next
    })
    setSavedMsg('')
  }

  async function handleSave() {
    if (!agent?.atera_id) return
    setSaving(true)
    setError('')
    setSavedMsg('')
    try {
      const data = await updateAgentPostureExclusions(agent.atera_id, {
        exclusions,
        note: note.trim() || null,
      })
      onSaved?.({
        posture_exclusions: data.exclusions,
        posture_exclusion_note: data.note,
      })
      setSavedMsg('Ignored checks saved.')
    } catch {
      setError('Could not save ignored checks.')
    } finally {
      setSaving(false)
    }
  }

  if (!isStaff) {
    if (activeCount === 0 && !savedNote) return null
    return (
      <CollapsePanel
        id={`posture-exclusions-${agent?.atera_id}`}
        title="Ignored checks"
        collapsedSummary={summary}
        open={open}
        onOpenChange={setOpen}
        bodyClassName="space-y-2 text-sm"
      >
        {labels.length > 0 ? (
          <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
            {labels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        ) : null}
        {savedNote ? <p className="text-sm text-muted-foreground">{savedNote}</p> : null}
      </CollapsePanel>
    )
  }

  return (
    <CollapsePanel
      id={`posture-exclusions-${agent?.atera_id}`}
      title="Ignored checks"
      collapsedSummary={summary}
      open={open}
      onOpenChange={setOpen}
      bodyClassName="space-y-4"
    >
      <p className="text-xs text-muted-foreground">
        Skip selected checks for this device when they will never be remediated (for example an
        intentional low-RAM Linux VM). Active threats are never excluded.
      </p>

      {groups.map(([group, options]) => (
        <div key={group} className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {group}
          </p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {options.map((opt) => (
              <li key={opt.key}>
                <label className="inline-flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-border"
                    checked={Boolean(exclusions[opt.key])}
                    onChange={() => toggle(opt.key)}
                  />
                  <span>{opt.label}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="space-y-2">
        <Label htmlFor={`posture-note-${agent?.atera_id}`}>Note</Label>
        <textarea
          id={`posture-note-${agent?.atera_id}`}
          className="min-h-[72px] w-full rounded-md border border-input bg-secondary/50 px-3 py-2 text-sm text-foreground"
          placeholder="e.g. Intentional 3 GB Linux VM — leave as is"
          maxLength={500}
          value={note}
          onChange={(e) => {
            setNote(e.target.value)
            setSavedMsg('')
          }}
        />
        <p className="text-xs text-muted-foreground">{note.length}/500</p>
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {savedMsg ? <p className="text-sm text-muted-foreground">{savedMsg}</p> : null}

      <Button
        type="button"
        size="sm"
        onClick={handleSave}
        disabled={saving || !dirty}
      >
        {saving ? 'Saving…' : 'Save exclusions'}
      </Button>
    </CollapsePanel>
  )
}
